import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import PriceCell from '../components/PriceCell';

interface Position {
    id: string;
    market: string;
    symbol: string;
    status: string;
    openedAt: string;
    closedAt: string | null;
    avgPrice: number;
    totalQty: number;
    realizedPnlPct: number | null;
    effectiveStopLossPct: number;
    attachmentCount: number;
    hasReview: boolean;
    fills?: Array<{
        type: 'BUY' | 'SELL';
        price: number;
        qty: number;
    }>;
}

export default function Positions() {
    const [positions, setPositions] = useState<Position[]>([]);
    const [filter, setFilter] = useState<'ALL' | 'OPEN' | 'CLOSED'>('ALL');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadPositions();
    }, [filter]);

    async function loadPositions() {
        setIsLoading(true);
        const status = filter === 'ALL' ? undefined : filter;
        const response = await api.getPositions(status);
        if (response.success) {
            setPositions(response.data || []);
        }
        setIsLoading(false);
    }

    const formatPnl = (pnl: number | null | undefined) => {
        if (pnl == null) return '-';
        const formatted = pnl.toFixed(2);
        return pnl >= 0 ? `+${formatted}%` : `${formatted}%`;
    };

    const getPnlClass = (pnl: number | null | undefined) => {
        if (pnl == null) return '';
        return pnl >= 0 ? 'positive' : 'negative';
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const openCount = positions.filter(p => p.status === 'OPEN').length;
    const closedCount = positions.filter(p => p.status === 'CLOSED').length;

    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">포트폴리오</h1>
                <Link to="/new" className="btn btn-primary">
                    + 포트폴리오 등록
                </Link>
            </div>

            {/* Stats */}
            <div className="stats-grid mb-lg">
                <div className="stat-card">
                    <div className="stat-value">{positions.length}</div>
                    <div className="stat-label">전체</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value text-success">{openCount}</div>
                    <div className="stat-label">보유중</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">{closedCount}</div>
                    <div className="stat-label">청산</div>
                </div>
            </div>

            {/* Tabs */}
            {/* Tabs */}
            <div className="tabs mb-md" style={{ display: 'flex', gap: '8px' }}>
                <button
                    className={`btn ${filter === 'ALL' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFilter('ALL')}
                >
                    전체
                </button>
                <button
                    className={`btn ${filter === 'OPEN' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFilter('OPEN')}
                >
                    보유중
                </button>
                <button
                    className={`btn ${filter === 'CLOSED' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFilter('CLOSED')}
                >
                    청산
                </button>
            </div>

            {isLoading ? (
                <div className="loading-screen">
                    <div className="loading-spinner"></div>
                </div>
            ) : positions.length === 0 ? (
                <div className="card">
                    <div className="empty-state">
                        <p>포트폴리오가 없습니다</p>
                        <Link to="/new" className="btn btn-primary mt-md">첫 포트폴리오 기록하기</Link>
                    </div>
                </div>
            ) : (
                <div className="card">
                    <div className="table-container">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>종목</th>
                                    <th>시장</th>
                                    <th>상태</th>
                                    <th>평단</th>
                                    <th>수량</th>
                                    <th>수익률</th>
                                    <th>손절</th>
                                    <th>진입일</th>
                                    <th>첨부</th>
                                </tr>
                            </thead>
                            <tbody>
                                {positions.map((pos) => (
                                    <tr key={pos.id}>
                                        <td>
                                            <Link to={`/positions/${pos.id}`} className="font-semibold">
                                                {pos.symbol}
                                            </Link>
                                        </td>
                                        <td className="text-muted">{pos.market}</td>
                                        <td>
                                            <span className={`badge ${pos.status === 'OPEN' ? 'badge-success' : 'badge-neutral'}`}>
                                                {pos.status === 'OPEN' ? '보유중' : '청산'}
                                            </span>
                                            {pos.status === 'CLOSED' && !pos.hasReview && (
                                                <span className="badge badge-warning" style={{ marginLeft: '4px' }}>
                                                    복기 필요
                                                </span>
                                            )}
                                        </td>
                                        {/* Computed Stats */}
                                        {(() => {
                                            const buyFills = pos.fills?.filter(f => f.type === 'BUY') || [];
                                            const totalQty = buyFills.reduce((sum, f) => sum + f.qty, 0);
                                            const totalCost = buyFills.reduce((sum, f) => sum + (f.price * f.qty), 0);
                                            const avgPrice = totalQty > 0 ? totalCost / totalQty : 0;

                                            // Realized P&L is only relevant for CLOSED positions, otherwise show '-'
                                            // The stored realizedPnlPct in DB is fine to use for CLOSED positions

                                            return (
                                                <>
                                                    <td>{avgPrice > 0 ? avgPrice.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '-'}원</td>
                                                    <td>{totalQty > 0 ? totalQty.toLocaleString() : '-'}주</td>
                                                    <td className={pos.status === 'CLOSED' ? getPnlClass(pos.realizedPnlPct) : ''}>
                                                        {pos.status === 'CLOSED' ? (
                                                            formatPnl(pos.realizedPnlPct)
                                                        ) : (
                                                            avgPrice > 0 ? (
                                                                <PriceCell
                                                                    symbol={pos.symbol}
                                                                    market={pos.market}
                                                                    avgPrice={avgPrice}
                                                                />
                                                            ) : '-'
                                                        )}
                                                    </td>
                                                </>
                                            );
                                        })()}
                                        <td className="text-muted">{pos.effectiveStopLossPct ?? '-'}%</td>
                                        <td className="text-muted">{pos.openedAt ? formatDate(pos.openedAt) : '-'}</td>
                                        <td>
                                            {pos.attachmentCount > 0 ? (
                                                <span className="badge badge-info">{pos.attachmentCount}</span>
                                            ) : (
                                                <span className="badge badge-danger">0</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
